import { createTheme, Theme, ThemeOptions } from "@mui/material/styles";
import "../assets/fonts/aptos-fonts.css";
import { MeetingStatus } from "../types";

type MeetingStatusPaletteColor = {
  main: string;
  background: string;
};

type PatientChipPaletteColor = {
  main: string;
  background: string;
};

type MeetingStatusPalette = Record<MeetingStatus, MeetingStatusPaletteColor>;

declare module "@mui/material/styles" {
  interface Palette {
    patientChip: PatientChipPaletteColor;
    meetingStatus: MeetingStatusPalette;
  }

  interface PaletteOptions {
    patientChip?: Partial<PatientChipPaletteColor>;
    meetingStatus?: Partial<MeetingStatusPalette>;
  }
}

export const themeOptions: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      main: "#1184b5",
      light: "#90caf9",
    },
    secondary: {
      main: "#115bb5",
    },
    text: {
      primary: "#272727",
      secondary: "#555555",
    },
    patientChip: {
      main: "#0077e6",
      background: "#dbeafe",
    },
    meetingStatus: {
      [MeetingStatus.Scheduled]: {
        main: "#4286ce",
        background: "#dbeafe6e",
      },
      [MeetingStatus.Started]: {
        main: "#2aaa79",
        background: "#dbfee1",
      },
      [MeetingStatus.Cancelled]: {
        main: "#c04b3c",
        background: "#fedbdb",
      },
      [MeetingStatus.Training]: {
        main: "#d89a15",
        background: "#fff3db",
      },
      [MeetingStatus.Proposed]: {
        main: "#9c52c7",
        background: "#f3e8ff",
      },
      [MeetingStatus.Abandoned]: {
        main: "#7c4434",
        background: "#f1dbdb",
      },
      [MeetingStatus.Ended]: {
        main: "#7c7c7c",
        background: "#f1f1f1",
      },
    },
  },
  typography: {
    fontFamily: "Aptos Narrow, Lato, Nunito, Roboto, Helvetica, Arial, sans-serif",
    fontSize: 16,
    fontWeightBold: 600,
    h1: {
      fontWeight: 600,
      fontSize: "4rem",
      lineHeight: 1.21,
    },
    h2: {
      fontWeight: 600,
      fontSize: "2.5rem",
      lineHeight: 1.27,
    },
    h3: {
      fontWeight: 600,
      fontSize: "2rem",
      lineHeight: 1.33,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.5,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.3rem",
      lineHeight: 1.3,
    },
    h6: {
      fontWeight: 400,
      fontSize: "1rem",
      lineHeight: 1.57,
    },
    caption: {
      fontWeight: 400,
      fontSize: "0.8rem",
      lineHeight: 1,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.2,
    },
    body2: {
      fontSize: "0.88rem",
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: "0.875rem",
      fontWeight: 600,
      lineHeight: 1.57,
    },
    subtitle2: {
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: .8,
    },
    overline: {
      lineHeight: 1.66,
    },
    button: {
      textTransform: "capitalize",
    },
  },
  // make button have a gradient background, then flat on click
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          backgroundColor: '#2270b5',
          background: 'linear-gradient(0deg, #2270b5, #38bbcd)',
          color: "#FFFFFF",
          borderRadius: "0.2rem",
          '&:hover': {
            background: 'linear-gradient(0deg, #2270b5, #228cb7)',
            backgroundColor: 'transparent', // Override the default backgroundColor on hover
          },
          '&:disabled': {
            backgroundColor: "#f5f5f5",
            background: "#f5f5f5", // Override the gradient
            color: "#999",
            borderColor: "#BBB",
          },
        },
        containedError: {
          backgroundColor: '#d32f2f',
          background: 'linear-gradient(0deg, #d32f2f, #f44336)',
          color: "#FFFFFF",
          '&:hover': {
            background: 'linear-gradient(0deg, #c62828, #e53935)',
            backgroundColor: 'transparent',
          },
          '&:disabled': {
            backgroundColor: "#f5f5f5",
            background: "#f5f5f5",
            color: "#999",
          },
        },
        outlined: {
          borderColor: "#BBB",
          color: "#555",
          fontWeight: 700,
          '&:hover': {
            background: 'linear-gradient(0deg,rgba(34, 112, 181, 0.17),rgba(56, 188, 205, 0.19))',
            borderColor: "#BBB",
          },
          '&:disabled': {
            backgroundColor: "#f5f5f5",
            color: "#999",
            borderColor: "#BBB",
          },
        },
      },
    },
    // Mui Table
    MuiTable: {
      styleOverrides: {
        root: {
          width: "100%",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          background: "linear-gradient(0deg,rgb(217, 233, 247),rgb(217, 238, 247))",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          fontSize: "1rem",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "24px",
          paddingTop: 0,
        },
      },
    },
  }
};

export const theme: Theme = createTheme(themeOptions);
